import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardActionArea,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { PageHeader } from '../../shared/components/PageHeader';
import { FilterMultiSelect, type FilterOption } from '../../shared/components/FilterMultiSelect';
import { ConfirmDialog } from '../../shared/components/Dialogs/ConfirmDialog';
import { resources } from '../../core/services/resources';
import { useTenant } from '../../core/contexts/TenantContext';
import { flagFor } from '../../shared/utils/geo';
import type { CustomViewFilter, CustomViewWidget } from '../../core/types/data-models';
import { WidgetCard } from './WidgetCard';
import { WidgetDialog } from './WidgetDialog';
import {
  clampSpan,
  EMPTY_SELECTION,
  GRID_COLUMNS,
  GRID_ROW_HEIGHT,
  type CustomViewSelection,
} from './widget-utils';

/**
 * Custom Views canvas (`/custom-views/:id`): breadcrumb scope + the
 * Visibility-style cross-filtered filter row, then a 3-column widget grid
 * with an add-widget tile. Without an `:id` the page just points at the
 * sidebar selector.
 */
export function CustomViewPage() {
  const { id } = useParams();
  const viewId = id != null ? Number(id) : null;

  if (viewId == null || !Number.isFinite(viewId)) {
    return (
      <Box>
        <PageHeader title="Custom Views" />
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Select a custom view from the sidebar, or create a new one via{' '}
            <strong>New custom view</strong> in the custom-view dropdown.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Keyed by view id so all filter/widget state resets when switching views.
  return <CustomViewCanvas key={viewId} viewId={viewId} />;
}

/** Persisted filter rows → selection (unknown names / bad JSON ignored). */
function parseStoredFilters(rows: CustomViewFilter[]): CustomViewSelection {
  const selection = { ...EMPTY_SELECTION };
  for (const row of rows) {
    if (!(row.filterName in selection)) continue;
    try {
      const values = JSON.parse(row.filterValue);
      if (Array.isArray(values)) {
        selection[row.filterName as keyof CustomViewSelection] = values;
      }
    } catch {
      // Ignore unparseable rows — the filter just starts empty.
    }
  }
  return selection;
}

function CustomViewCanvas({ viewId }: { viewId: number }) {
  const { activeTenantName } = useTenant();
  const queryClient = useQueryClient();

  const [selection, setSelection] = useState<CustomViewSelection>(EMPTY_SELECTION);
  const [appliedSelection, setAppliedSelection] = useState<CustomViewSelection>(EMPTY_SELECTION);
  const [hydrated, setHydrated] = useState(false);
  const hasSelection = useMemo(
    () => Object.values(selection).some((arr) => arr.length > 0),
    [selection],
  );

  const [widgetDialogOpen, setWidgetDialogOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState<CustomViewWidget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CustomViewWidget | null>(null);

  // --- breadcrumb scope (tenant → LOB → custom view) ------------------------
  const view = useQuery({
    queryKey: ['custom-views', viewId],
    queryFn: () => resources.customViews.get(viewId),
  });
  const lineOfBizId = view.data?.lineOfBizId;
  const lineOfBiz = useQuery({
    queryKey: ['lines-of-business', lineOfBizId],
    queryFn: () => resources.linesOfBusiness.get(lineOfBizId as number),
    enabled: lineOfBizId != null,
  });

  // --- persisted view filters ------------------------------------------------
  const filtersQuery = useQuery({
    queryKey: ['custom-view-filters', viewId],
    queryFn: () => resources.customViewFiltersFor(viewId).list({ pageSize: 100 }),
  });
  // One-shot hydration: seed the live + applied selections from the stored
  // rows the first time they arrive. Later refetches must not clobber the
  // user's in-progress edits.
  if (!hydrated && filtersQuery.data) {
    const stored = parseStoredFilters(filtersQuery.data.data);
    setSelection(stored);
    setAppliedSelection(stored);
    setHydrated(true);
  }

  // "Show" persists the applied selection: replace-all of the view's
  // filter rows (one row per non-empty dimension, JSON-encoded values).
  const saveFilters = useMutation({
    mutationFn: async (sel: CustomViewSelection) => {
      const service = resources.customViewFiltersFor(viewId);
      const existing = await service.list({ pageSize: 100 });
      await Promise.all(existing.data.map((f) => service.remove(f.id)));
      const entries = Object.entries(sel).filter(([, values]) => values.length > 0);
      await Promise.all(
        entries.map(([filterName, values]) =>
          service.create({ filterName, filterValue: JSON.stringify(values) }),
        ),
      );
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['custom-view-filters', viewId] }),
  });

  // --- label catalogs (resolve numeric ids → human names) --------------------
  const geoCatalog = useQuery({
    queryKey: ['geo-locations', 'select'],
    queryFn: () => resources.geoLocations.list({ pageSize: 500, sort: 'name' }),
  });
  const geoById = useMemo(() => {
    const map = new Map<number, { code: string; name: string }>();
    for (const g of geoCatalog.data?.data ?? []) map.set(g.id, { code: g.code, name: g.name });
    return map;
  }, [geoCatalog.data]);

  const aiModelCatalog = useQuery({
    queryKey: ['ai-models', 'select'],
    queryFn: () => resources.aiModels.list({ pageSize: 500, sort: 'name' }),
  });
  const aiModelNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of aiModelCatalog.data?.data ?? []) map.set(m.id, m.name);
    return map;
  }, [aiModelCatalog.data]);

  const categoryCatalog = useQuery({
    queryKey: ['line-of-biz-categories', 'select', lineOfBizId],
    queryFn: () =>
      resources.lineOfBizCategories.list({
        pageSize: 500,
        sort: 'name',
        filter: lineOfBizId != null ? { lineOfBizId } : undefined,
      }),
    enabled: lineOfBizId != null,
  });
  const categoryNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of categoryCatalog.data?.data ?? []) map.set(c.id, c.name);
    return map;
  }, [categoryCatalog.data]);

  const personaCatalog = useQuery({
    queryKey: ['line-of-biz-personas', 'select', lineOfBizId],
    queryFn: () =>
      resources.lineOfBizPersonas.list({
        pageSize: 500,
        sort: 'name',
        filter: lineOfBizId != null ? { lineOfBizId } : undefined,
      }),
    enabled: lineOfBizId != null,
  });
  const personaNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of personaCatalog.data?.data ?? []) map.set(p.id, p.name);
    return map;
  }, [personaCatalog.data]);

  const stageCatalog = useQuery({
    queryKey: ['prompt-funnel-stages', 'select'],
    queryFn: () => resources.promptFunnelStages.list({ pageSize: 500, sort: 'name' }),
  });
  const stageNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of stageCatalog.data?.data ?? []) map.set(s.id, s.name);
    return map;
  }, [stageCatalog.data]);

  // --- cross-filtered options -------------------------------------------------
  const optionsQuery = useQuery({
    queryKey: ['report-instance-filter-options', 'custom-view', selection],
    queryFn: () => resources.reportInstanceFilterOptions({ ...selection }),
    placeholderData: keepPreviousData,
  });
  const opts = optionsQuery.data;
  const loading = optionsQuery.isFetching;

  const geoLabel = (geoId: number) => {
    const geo = geoById.get(geoId);
    return geo ? `${flagFor(geo.code)} ${geo.name}` : `#${geoId}`;
  };

  const reportInstanceOptions = useMemo<FilterOption<number>[]>(
    () => (opts?.reportInstanceIds ?? []).map((o) => ({ value: o.id, label: `#${o.id}` })),
    [opts],
  );
  const geoOptions = useMemo<FilterOption<number>[]>(
    () => (opts?.geoLocationIds ?? []).map((o) => ({ value: o.id, label: geoLabel(o.id) })),
    [opts, geoById],
  );
  const aiModelOptions = useMemo<FilterOption<number>[]>(
    () =>
      (opts?.aiModelIds ?? []).map((o) => ({ value: o.id, label: aiModelNameById.get(o.id) ?? o.name })),
    [opts, aiModelNameById],
  );
  const categoryOptions = useMemo<FilterOption<number>[]>(
    () =>
      (opts?.categoryIds ?? []).map((o) => ({ value: o.id, label: categoryNameById.get(o.id) ?? o.name })),
    [opts, categoryNameById],
  );
  const personaOptions = useMemo<FilterOption<number>[]>(
    () =>
      (opts?.personaIds ?? []).map((o) => ({ value: o.id, label: personaNameById.get(o.id) ?? o.name })),
    [opts, personaNameById],
  );
  const stageOptions = useMemo<FilterOption<number>[]>(
    () => (opts?.stageIds ?? []).map((o) => ({ value: o.id, label: stageNameById.get(o.id) ?? o.name })),
    [opts, stageNameById],
  );
  const intentionOptions = useMemo<FilterOption<string>[]>(
    () => (opts?.intentionTypes ?? []).map((v) => ({ value: v, label: v })),
    [opts],
  );

  // --- widgets -----------------------------------------------------------------
  const widgetsQuery = useQuery({
    queryKey: ['custom-view-widgets', viewId],
    queryFn: () => resources.customViewWidgetsFor(viewId).list({ pageSize: 100, sort: 'order' }),
  });
  const widgets = widgetsQuery.data?.data ?? [];
  const nextOrder = widgets.reduce((max, w) => Math.max(max, w.order), 0) + 1;

  const deleteWidget = useMutation({
    mutationFn: (widgetId: number) => resources.customViewWidgetsFor(viewId).remove(widgetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-view-widgets', viewId] });
      setDeleteTarget(null);
    },
  });

  const tenantName = activeTenantName ?? 'Tenant';
  const lobName = lineOfBiz.data?.name ?? (lineOfBizId != null ? `LOB #${lineOfBizId}` : '—');
  const viewName = view.data?.name ?? `Custom view #${viewId}`;

  // Refetch the widget list plus every widget's analytics data. The list
  // refetch also picks up requests regenerated by the LLM agent; the data
  // prefix invalidation re-runs each widget's flat-view query.
  const refreshWidgets = () => {
    queryClient.invalidateQueries({ queryKey: ['custom-view-widgets', viewId] });
    queryClient.invalidateQueries({ queryKey: ['custom-view-widget-data'] });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader
        title={viewName}
        actions={
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={refreshWidgets}>
            Refresh
          </Button>
        }
      />

      {/* Top panel: active scope + cross-filtered dropdown row. */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {tenantName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {lobName}
          </Typography>
          <Typography variant="body2" fontWeight={600} color="text.primary">
            {viewName}
          </Typography>
        </Breadcrumbs>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
            gap: 1.5,
          }}
        >
          <FilterMultiSelect
            label="Report instances"
            options={reportInstanceOptions}
            value={selection.reportInstanceIds}
            onChange={(v) => setSelection((s) => ({ ...s, reportInstanceIds: v }))}
            loading={loading}
          />
          <FilterMultiSelect
            label="Geo locations"
            options={geoOptions}
            value={selection.geoLocationIds}
            onChange={(v) => setSelection((s) => ({ ...s, geoLocationIds: v }))}
            loading={loading}
          />
          <FilterMultiSelect
            label="AI models"
            options={aiModelOptions}
            value={selection.aiModelIds}
            onChange={(v) => setSelection((s) => ({ ...s, aiModelIds: v }))}
            loading={loading}
          />
          <FilterMultiSelect
            label="Categories"
            options={categoryOptions}
            value={selection.categoryIds}
            onChange={(v) => setSelection((s) => ({ ...s, categoryIds: v }))}
            loading={loading}
          />
          <FilterMultiSelect
            label="Personas"
            options={personaOptions}
            value={selection.personaIds}
            onChange={(v) => setSelection((s) => ({ ...s, personaIds: v }))}
            loading={loading}
          />
          <FilterMultiSelect
            label="Funnel stages"
            options={stageOptions}
            value={selection.stageIds}
            onChange={(v) => setSelection((s) => ({ ...s, stageIds: v }))}
            loading={loading}
          />
          <FilterMultiSelect
            label="Intention types"
            options={intentionOptions}
            value={selection.intentionTypes}
            onChange={(v) => setSelection((s) => ({ ...s, intentionTypes: v }))}
            loading={loading}
          />
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              size="small"
              disabled={!hasSelection}
              onClick={() => {
                setSelection(EMPTY_SELECTION);
                setAppliedSelection(EMPTY_SELECTION);
                saveFilters.mutate(EMPTY_SELECTION);
              }}
            >
              Clear filters
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={saveFilters.isPending}
              onClick={() => {
                setAppliedSelection(selection);
                saveFilters.mutate(selection);
              }}
            >
              Show
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Widget grid: 4 columns of fixed-height rows; each widget spans
          its configured width (columns) and height (rows). The trailing
          tile adds a new widget. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: `repeat(${GRID_COLUMNS}, 1fr)` },
          gridAutoRows: GRID_ROW_HEIGHT,
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        {widgets.map((w) => (
          <Box
            key={w.id}
            sx={{
              gridColumn: { xs: 'span 1', md: `span ${clampSpan(w.size)}` },
              gridRow: `span ${clampSpan(w.rows)}`,
              minWidth: 0,
            }}
          >
            <WidgetCard
              widget={w}
              selection={appliedSelection}
              onEdit={() => {
                setEditingWidget(w);
                setWidgetDialogOpen(true);
              }}
              onDelete={() => setDeleteTarget(w)}
            />
          </Box>
        ))}
        <Card
          variant="outlined"
          sx={{ borderStyle: 'dashed', height: '100%', display: 'flex' }}
        >
          <CardActionArea
            onClick={() => {
              setEditingWidget(null);
              setWidgetDialogOpen(true);
            }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <AddIcon color="primary" />
            <Typography variant="body2" color="primary" fontWeight={600}>
              Add widget
            </Typography>
          </CardActionArea>
        </Card>
      </Box>

      <WidgetDialog
        open={widgetDialogOpen}
        onClose={() => setWidgetDialogOpen(false)}
        customViewId={viewId}
        widget={editingWidget}
        nextOrder={nextOrder}
      />

      <ConfirmDialog
        open={deleteTarget != null}
        title="Delete widget"
        message={`Delete widget "${deleteTarget?.title ?? ''}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={deleteWidget.isPending}
        onConfirm={() => deleteTarget && deleteWidget.mutate(deleteTarget.id)}
        onClose={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
